import angular, { IPromise } from 'angular'
import extend from 'lodash/extend'
import some from 'lodash/some'
import { ApiRetailer, RetailerBarcodeOption } from '@vend/types'

import { WEB_REGISTER } from '../components/web-register.constant'
import { RetailerSettingsStore } from '../factories/database/retailer-settings-store'
import { cleanExtend } from '../factories/helpers/cleanExtend'
import { $rootScope } from '../ng-compat/imports'
import { SyncManager } from './sync-manager'

/**
 * Manages retailer settings - providing an in memory cache for the settings stored in IndexedDB for synchronous
 * access.
 *
 * A single retailer settings object is returned and then updated; if templates bind directly to this object,
 * they will get changes without having to listen for change events.
 */

export class RetailerSettingsManager {
  // This conversion is unsafe, but RetailerSettingsManager is initialised very early on, so
  // the ApiRetailer type makes sense for most of the application's lifecycle.
  // Otherwise we could use Partial<ApiRetailer> or similar, but would have to convert in many other places.
  private retailerSettings: ApiRetailer = {} as ApiRetailer
  private _whenInitialised?: IPromise<ApiRetailer>

  constructor(
    private retailerSettingsStore: RetailerSettingsStore,
    private syncManager: SyncManager
  ) {
    'ngInject'
  }

  /**
   * Initialise the retailer settings manager, fetching the settings from IndexedDB.
   *
   * @return {Promise} A promise resolved once the manager has been initialised.
   */
  public init(): IPromise<ApiRetailer> {
    if (!this._whenInitialised) {
      this._whenInitialised = this.syncManager
        .whenCompleteEntityDataAvailable('retailerSettings')
        .then(() => this.retailerSettingsStore.get())
        .then((settings: ApiRetailer) => {
          extend(this.retailerSettings, settings || {})

          this.retailerSettingsStore.on('sync', event => {
            // Replace all properties
            $rootScope.$applyAsync(() =>
              cleanExtend(this.retailerSettings, event.updated)
            )
          })

          return this.retailerSettings
        })
    }

    return this._whenInitialised
  }

  /**
   * Get the retailer's settings.
   *
   * @return {Object} The retailer's settings
   */
  public get(): ApiRetailer {
    return this.retailerSettings
  }

  /**
   * Returns the url for the retailer.
   *
   * @return {String} The url for the retailer
   */
  public getRetailerUrl(): string {
    const RETAILER_DOMAIN = this.retailerSettings.domain_prefix
    const APP_URI =
      WEB_REGISTER.LS_URI && window.location.href.includes(WEB_REGISTER.LS_URI)
        ? WEB_REGISTER.LS_URI
        : WEB_REGISTER.VEND_URI
    return `https://${RETAILER_DOMAIN}.${APP_URI}`
  }

  /**
   * Whether loyalty is enabled for this retailer.
   *
   * @return {Boolean} true if loyalty is enabled for the retailer, false otherwise
   */
  public isLoyaltyEnabled(): boolean {
    return Boolean(
      this.retailerSettings.loyalty && this.retailerSettings.loyalty.enabled
    )
  }

  /**
   * Get the loyalty ratio for the retailer.
   *
   * @return {Number} The ratio to the $ that loyalty is earned for the retailer (or 0 if loyalty is not enabled)
   */
  public getLoyaltyRatio(): number {
    return this.isLoyaltyEnabled() ? this.retailerSettings.loyalty.ratio : 0
  }

  /**
   * Check if store credit is enabled for the retailer
   *
   * @return {Boolean} Returns true if enabled, false otherwise
   */
  public isStoreCreditEnabled(): boolean {
    return Boolean(this.retailerSettings.store_credit)
  }

  /**
   * Get the option setting for embedded barcodes
   *
   * @return {String} Barcode option setting
   */
  public getBarcodeOption(): RetailerBarcodeOption {
    return this.retailerSettings.embedded_barcode_option
  }

  /**
   * Check if xero integration is enabled
   *
   * @return {Boolean} true if the retailer have xero integration enabled, false otherwise
   */
  public isXeroIntegrationEnabled(): boolean {
    let xeroIntegration = false

    some(this.retailerSettings.enabled_integrations, integration => {
      if (integration.name === 'Xero') {
        xeroIntegration = true
        return true
      }
    })

    return xeroIntegration
  }
}

angular
  .module('webregister.managers')
  .service('retailerSettingsManager', RetailerSettingsManager)
